import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Stack,
  Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { ApiService } from '../../../services/api';
import { CropEmbeddingSearchResponse, ProductVariantResponse } from '../../../types';
import { ImageZoomModal } from './ImageZoomModal';

interface VariantSelectionModalProps {
  open: boolean;
  loading: boolean;
  variants: ProductVariantResponse[];
  token: string;
  chainId: number | undefined;
  onSelect: (variant: ProductVariantResponse) => void;
  onClose: () => void;
}

export const VariantSelectionModal: React.FC<VariantSelectionModalProps> = ({
  open,
  loading,
  variants,
  token,
  chainId,
  onSelect,
  onClose,
}) => {
  const [selectedUpc, setSelectedUpc] = useState<string | null>(null);
  const [embeddingsByUpc, setEmbeddingsByUpc] = useState<
    Record<string, CropEmbeddingSearchResponse[]>
  >({});
  const [embeddingsLoadingUpcs, setEmbeddingsLoadingUpcs] = useState<Set<string>>(new Set());
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);

  // Case UPCs aren't valid design variants — hide them entirely.
  const selectableVariants = useMemo(() => variants.filter((v) => !v.isCaseUpc), [variants]);

  useEffect(() => {
    if (!open) {
      setSelectedUpc(null);
      setEmbeddingsByUpc({});
      setEmbeddingsLoadingUpcs(new Set());
      setZoomSrc(null);
      return;
    }
    if (!token || !chainId || selectableVariants.length === 0) return;

    let cancelled = false;
    setEmbeddingsByUpc({});
    setEmbeddingsLoadingUpcs(new Set(selectableVariants.map((v) => v.upc)));

    selectableVariants.forEach(async (variant) => {
      let results: CropEmbeddingSearchResponse[] = [];
      try {
        results = await ApiService.searchEmbeddings(token, {
          chainId,
          normalizedBarcodes: variant.upc,
          isActive: true,
          source: 'scan',
          limit: 3,
        });
      } catch {
        // swallow — show empty list for this variant
      }
      if (cancelled) return;
      setEmbeddingsByUpc((prev) => ({ ...prev, [variant.upc]: results }));
      setEmbeddingsLoadingUpcs((prev) => {
        const next = new Set(prev);
        next.delete(variant.upc);
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [open, token, chainId, selectableVariants]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth={false}>
        <DialogTitle>Select variant</DialogTitle>
        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : selectableVariants.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No variants available for this product.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {selectableVariants.map((variant) => {
                const isSelected = selectedUpc === variant.upc;
                const embeddings = embeddingsByUpc[variant.upc] ?? [];
                const isEmbeddingsLoading = embeddingsLoadingUpcs.has(variant.upc);
                return (
                  <Box
                    key={variant.upc}
                    onClick={() => setSelectedUpc(variant.upc)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      p: 1,
                      borderRadius: 1,
                      cursor: 'pointer',
                      border: '1px solid',
                      borderColor: isSelected ? '#1976d2' : 'divider',
                      backgroundColor: isSelected ? '#E3F2FD' : 'transparent',
                      '&:hover': { backgroundColor: isSelected ? '#E3F2FD' : 'action.hover' },
                    }}
                  >
                    <Radio checked={isSelected} size="small" />
                    {variant.frontImage && (
                      <Box
                        component="img"
                        src={variant.frontImage}
                        alt={variant.name || variant.upc}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (variant.frontImage) setZoomSrc(variant.frontImage);
                        }}
                        sx={{
                          width: 70,
                          height: 70,
                          objectFit: 'contain',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: '#fff',
                          cursor: 'zoom-in',
                        }}
                      />
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {variant.name || 'Unknown'}
                        </Typography>
                        {variant.isActive === false && (
                          <Chip
                            icon={<WarningAmberOutlinedIcon style={{ fontSize: 14 }} />}
                            label="Inactive"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '11px',
                              fontWeight: 500,
                              backgroundColor: '#FEF3C7',
                              color: '#92400E',
                              '& .MuiChip-icon': { color: '#B45309', marginLeft: '4px' },
                            }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {variant.upc}
                      </Typography>
                    </Box>
                    {(isEmbeddingsLoading || embeddings.length > 0) && (
                      <>
                        <Box
                          sx={{
                            width: '1px',
                            alignSelf: 'stretch',
                            backgroundColor: 'divider',
                            mx: 0.5,
                          }}
                        />
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 0.5,
                            flexShrink: 0,
                            minWidth: 70,
                            minHeight: 70,
                          }}
                        >
                          {isEmbeddingsLoading ? (
                            <CircularProgress size={16} />
                          ) : (
                            embeddings.map((emb) => (
                              <Box
                                key={emb.productEmbeddingCropId ?? emb.vizUrl}
                                component="img"
                                src={emb.vizUrl}
                                alt="embedding"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setZoomSrc(emb.vizUrl);
                                }}
                                sx={{
                                  width: 70,
                                  height: 70,
                                  objectFit: 'contain',
                                  borderRadius: 0.5,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  backgroundColor: '#fff',
                                  cursor: 'zoom-in',
                                }}
                              />
                            ))
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                );
              })}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!selectedUpc || loading}
            onClick={() => {
              const variant = selectableVariants.find((v) => v.upc === selectedUpc);
              if (variant) onSelect(variant);
            }}
          >
            Create new design
          </Button>
        </DialogActions>
      </Dialog>
      {zoomSrc && (
        <ImageZoomModal isOpen={true} imageSrc={zoomSrc} onClose={() => setZoomSrc(null)} />
      )}
    </>
  );
};
